import { toAudio, toOriginal } from "./timeline.js"
import { clearPendingSeek, intendedPosition, markProgrammaticSeek, setPendingSeek } from "./programmaticSeek.js"

export const MEDIA_SLOT = "media"
export const STANDBY_SLOT = "standby"

const pendingApply = new WeakMap()

/**
 * Sets currentTime now and again once metadata is known: iOS Safari ignores
 * a currentTime set on an element that has not loaded its metadata yet.
 * Until then the target is registered as the element's intended position,
 * so the audio play beacon (which also waits for loadedmetadata and may run
 * first) logs the mapped second. A newer call replaces an older pending one.
 */
export function seekWhenReady(el, seconds) {
    const previous = pendingApply.get(el)

    if (previous) {
        el.removeEventListener("loadedmetadata", previous)
        pendingApply.delete(el)
    }

    markProgrammaticSeek(el)
    el.currentTime = seconds

    if (el.readyState >= 1) {
        clearPendingSeek(el)

        return
    }

    const apply = () => {
        el.removeEventListener("loadedmetadata", apply)
        pendingApply.delete(el)
        clearPendingSeek(el)
        markProgrammaticSeek(el)
        el.currentTime = seconds
    }

    setPendingSeek(el, seconds)
    pendingApply.set(el, apply)
    el.addEventListener("loadedmetadata", apply)
}

export const rebindWithHandleMediaUpdated = (controller, el) => controller.handleMediaUpdated(el)

export const rebindByReinserting = (controller, el) => controller.insertBefore(el, controller.firstChild)

/**
 * Audio/video switch inside one <media-controller>. Only one element carries
 * slot="media"; the other waits in an unassigned slot (not rendered). The
 * controller's MutationObserver only watches childList, so moving the slot
 * attribute is followed by an explicit rebind. The default calls
 * handleMediaUpdated() (public API on MediaContainer), which makes the
 * media store tear down the old element and bind the new one.
 *
 * `rebind` is injectable for the fallback in case a browser proves the
 * default insufficient: re-inserting the element goes through the
 * controller's own childList MutationObserver path instead.
 *
 * `playback` (useVideoPlayback) stops the video stream from loading while
 * the audio plays, so a paused <video> does not keep fetching segments
 * against the traffic quota, and restarts it at the mapped position on the
 * way back. Both calls are synchronous (no-ops on native HLS).
 *
 * switchTo() runs synchronously up to and including play(), so calling it
 * from a click handler keeps the user gesture iOS needs.
 *
 * initialMode / mapPosition / { play } (SC-1323): the portal starts in
 * either mode, maps to a separate item's own position, and switches on
 * init without playing. The defaults are the podcast switch, unchanged.
 */
const noPlayback = { stopLoad() {}, startLoad() {} }

export function createMediaSwitch({ controller, video, audio, audioUrl, insertions, rebind = rebindWithHandleMediaUpdated, playback = noPlayback, initialMode = "video", mapPosition = null }) {
    let mode = initialMode
    let audioSourceSet = false
    // SC-1323: separate portal items map to the target's own resume position instead.
    const map = mapPosition ?? ((next, position) => (next === "audio" ? toAudio(position, insertions) : toOriginal(position, insertions)))

    // Nothing switches into an initially active audio element, so its source goes on now.
    if (initialMode === "audio") {
        audio.src = audioUrl
        audioSourceSet = true
    }

    function switchTo(next, { play = true } = {}) {
        if (next === mode || (next !== "audio" && next !== "video")) {
            return null
        }

        const from = mode === "video" ? video : audio
        const to = next === "video" ? video : audio
        // A switch back before the audio metadata loaded: iOS has dropped
        // the audio's currentTime, the pending seek target still holds it.
        const position = intendedPosition(from)
        const target = map(next, position)

        from.pause()

        if (next === "audio") {
            playback.stopLoad()
        } else {
            playback.startLoad(target)
        }

        if (next === "audio" && !audioSourceSet) {
            audio.src = audioUrl
            audioSourceSet = true
        }

        seekWhenReady(to, target)

        from.setAttribute("slot", STANDBY_SLOT)
        to.setAttribute("slot", MEDIA_SLOT)
        rebind(controller, to)
        mode = next

        return play ? to.play() : Promise.resolve()
    }

    return {
        switchTo,
        get mode() {
            return mode
        },
    }
}
